import { NodeId } from 'web';
import { useCallback } from 'react';
import { Box, Slider, Typography } from '@mui/material';
import { PointCloudFromDepthData } from '@/types/node';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { color } from '@/colors';
import { FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import ThreeHandleSlider from '@/UI/ThreeHandleSlider/ThreeHandleSlider';
import { usePointCloudView, useThreeDView } from '../Recipe/FlowContext';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { ThreeDViewer } from './Shared/FileViewer';
import { Lock3DOverlay } from './ModelComponents/FilesViewOverlay/AssetMetadataToolbar';

const MIN_DEPTH_SCALE = 0.1;
const MAX_DEPTH_SCALE = 20;
const DEPTH_SCALE_STEP = 0.1;

const gammaToPosition = (gamma: number, min: number, max: number): number => {
  const t = (Math.log10(gamma) + 1) / 2;
  return min + (max - min) * t;
};

const positionToGamma = (pos: number, min: number, max: number): number => {
  const range = max - min;
  if (range === 0) return 1.0;
  const t = (pos - min) / range;
  return Math.pow(10, t * 2 - 1);
};

function PointCloudFromDepthNode({ id, data }: { id: NodeId; data: PointCloudFromDepthData }) {
  const role = useUserWorkflowRole();
  const editable = data.isLocked !== true && role === 'editor';
  const threeDView = useThreeDView(id);
  const pointCloudView = usePointCloudView(id);

  const options = data.options;
  const depthScale = options.depthScale;
  const minDepth = options.minDepth * 255;
  const maxDepth = options.maxDepth * 255;
  const gammaDepth = options.gammaDepth;
  const middlePos = gammaToPosition(gammaDepth, minDepth, maxDepth);

  const updateIs3DLocked = useCallback(
    (locked: boolean) => {
      threeDView.setCameraLocked(locked);
    },
    [threeDView],
  );

  const handleScaleSliderChange = useCallback(
    (_: unknown, newValue: number | number[]) => {
      pointCloudView.setDepthScale(Array.isArray(newValue) ? newValue[0] : newValue, true);
    },
    [pointCloudView],
  );

  const handleScaleSliderCommitted = useCallback(
    (_: unknown, newValue: number | number[]) => {
      pointCloudView.setDepthScale(Array.isArray(newValue) ? newValue[0] : newValue, false);
    },
    [pointCloudView],
  );

  const handleScaleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      pointCloudView.setDepthScale(Number(e.target.value), false);
    },
    [pointCloudView],
  );

  const handleLevelsChange = useCallback(
    (values: { min: number; middle: number; max: number }, activeThumb: number, ongoing: boolean) => {
      const newMin = values.min / 255;
      const newMax = values.max / 255;
      let newGamma = gammaDepth;
      if (activeThumb === 1) {
        newGamma = positionToGamma(values.middle, values.min, values.max);
        newGamma = Math.max(0.1, Math.min(10, newGamma));
      }
      pointCloudView.setDepthLevels(newMin, newMax, newGamma, ongoing);
    },
    [pointCloudView, gammaDepth],
  );

  return (
    <>
      <DynamicNode2 id={id} data={data} className="point-cloud-from-depth">
        <ThreeDViewer id={id} />
        {editable ? (
          <Lock3DOverlay setIs3DLocked={updateIs3DLocked} is3DLocked={data.cameraLocked ?? false} bottomOffset={86} />
        ) : null}
        <Box sx={{ width: '100%', cursor: 'default' }} className="nodrag">
          <FlexCenVer sx={{ pt: 1, gap: 1, width: '100%' }}>
            <Typography variant="body-sm-rg" sx={{ color: color.White64_T, whiteSpace: 'nowrap' }}>
              Scale
            </Typography>
            <FlexCenHorVer sx={{ width: '100%' }}>
              <Slider
                size="small"
                value={depthScale}
                onChange={handleScaleSliderChange}
                onChangeCommitted={handleScaleSliderCommitted}
                aria-labelledby="depth-scale-slider"
                min={MIN_DEPTH_SCALE}
                max={MAX_DEPTH_SCALE}
                step={DEPTH_SCALE_STEP}
                disabled={!editable}
                slotProps={{
                  root: { style: { padding: '8px 0' } },
                }}
              />
            </FlexCenHorVer>
            <Input
              value={depthScale}
              disabled={!editable}
              size="small"
              onChange={handleScaleInputChange}
              sx={{ maxWidth: '52px' }}
              inputProps={{
                step: DEPTH_SCALE_STEP,
                min: MIN_DEPTH_SCALE,
                max: MAX_DEPTH_SCALE,
                type: 'number',
                'aria-labelledby': 'depth-scale-slider',
              }}
            />
          </FlexCenVer>
          <FlexCenVer sx={{ pt: 1, gap: 1, width: '100%' }}>
            <Typography variant="body-sm-rg" sx={{ color: color.White64_T, whiteSpace: 'nowrap' }}>
              Levels
            </Typography>
            <FlexCol sx={{ width: '100%' }}>
              <ThreeHandleSlider
                minValue={minDepth}
                maxValue={maxDepth}
                middleValue={middlePos}
                minLimit={0}
                maxLimit={255}
                step={1}
                disabled={!editable}
                onChange={(values, activeThumb) => handleLevelsChange(values, activeThumb, true)}
                onChangeCommitted={(values, activeThumb) => handleLevelsChange(values, activeThumb, false)}
              />
              <FlexCenHorVer sx={{ width: '100%', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
                  {minDepth.toFixed(0)}
                </Typography>
                <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
                  {gammaDepth.toFixed(2)}
                </Typography>
                <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
                  {maxDepth.toFixed(0)}
                </Typography>
              </FlexCenHorVer>
            </FlexCol>
          </FlexCenVer>
        </Box>
      </DynamicNode2>
    </>
  );
}

export default PointCloudFromDepthNode;
